import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import ApiErrorListener from "./ApiErrorListener";
import ApiStateListeners from "./ApiStateListeners";
import { apiUrl } from "./Config";
import StateEvent from "./StateEvent";

// The below can't be in a .d.ts file with the current config
declare global {
	type CustomResponse<T> = Promise<AxiosResponse<T, any>>
	type CustomResponseWithPagination<T> = Promise<AxiosResponse<
		T & {
			count: string | number
			limit: string | number
			limit_from: string | number
		},
		any
	>>
	type CustomResponseBrowse<T> = Promise<AxiosResponse<T & BrowseResponse, any>>
}

const statusCodesToSkip = [
	404,
	403,
];

const Common = class {
	headers?: ApiHeaders;
	apiUrl: string;
	skipGlobalErrorCatcher: boolean

	constructor(headers?: ApiHeaders) {
		this.headers = headers
		this.apiUrl = apiUrl
		this.skipGlobalErrorCatcher = false
	}

	setHeaders(headers: ApiHeaders) {
		this.headers = headers;
	}

	catchError(reason: any, reject: (o: StringObject) => void) {
		if (reason.response.status === 401) {
			ApiStateListeners.fireStateListeners(StateEvent.invalidToken);
		}

		// Append the error from our API
		if(
			reason?.response?.status &&
			statusCodesToSkip.indexOf(reason.response.status) === -1
		) {
			reason.customDescription = reason?.response?.data?.description

			if(!this.skipGlobalErrorCatcher) {
				ApiErrorListener.fireListeners(reason.response.status, reason.customDescription ?? "")
			}
		}

		reject(reason);
	}

	get(url: string, options: any = {}, options2: any = {}): Promise<AxiosResponse> {
		return new Promise((resolve, reject) => {
			axios.get(
				this.apiUrl + url,
				Object.assign({}, this.headers, { params: options, ...options2 })
			)
			.then(resolve)
			.catch((reason) => this.catchError(reason, reject))
		})
	}

	getNoBaseURL(url: string, options: StringObject = {}): Promise<AxiosResponse> {
		return new Promise((resolve, reject) => {
			axios.get(
				url,
				Object.assign({}, this.headers, { params: options })
			)
			.then(resolve)
			.catch((reason) => this.catchError(reason, reject))
		})
	}

	getNoHeaders(url: string): Promise<AxiosResponse> {
		return new Promise((resolve, reject) => {
			axios.get(
				this.apiUrl + url,
			)
			.then(resolve)
			.catch(reject)
		})
	}

	post(url: string, data = {}, options: AxiosRequestConfig = {}): Promise<AxiosResponse> {
		return new Promise((resolve, reject) => {
			axios.post(
				this.apiUrl + url,
				data,
				Object.assign({}, this.headers, options)
			)
			.then(resolve)
			.catch((reason) => this.catchError(reason, reject))
		})
	}

	postNoHeaders(url: string, data = {}): Promise<AxiosResponse> {
		return new Promise((resolve, reject) => {
			axios.post(
				this.apiUrl + url,
				data
			)
			.then(resolve)
			.catch(reject)
		})
	}

	doDelete(url: string, options: StringObject = {}, options2: StringObject = {}): Promise<AxiosResponse> {
		return new Promise((resolve, reject) => {
			axios.delete(
				this.apiUrl + url,
				Object.assign({}, this.headers, { params: options, ...options2 })
			)
			.then(resolve)
			.catch(reject)
		})
	}

	patch(url: string, data = {}, options: AxiosRequestConfig = {}): Promise<AxiosResponse> {
		return new Promise((resolve, reject) => {
			axios.patch(
				this.apiUrl + url,
				data,
				Object.assign({}, this.headers, options)
			)
			.then(resolve)
			.catch((reason) => this.catchError(reason, reject))
		})
	}

	getGETVariables(gets: {[key: string]: any}) {
		if(!gets || Object.keys(gets).length === 0) {
			return ''
		}

		const a = [];

		for(const key in gets) {
			if(Array.isArray(gets[key])) {
				gets[key].forEach((i: any) => a.push(encodeURIComponent(key + "[]") + '=' + encodeURIComponent(i)))
			} else {
				a.push(encodeURIComponent(key) + '=' + encodeURIComponent(gets[key]));
			}
		}

		return '?' + a.join('&');
	}
}

export default Common
